<template>
    <pc-categorized-filter
        ref="categorizedFilter"
        v-bind:items="itemsFunction"
        v-bind:selected="selected"
        v-bind:categories="categories"
        v-bind:header-override="fieldsHeader"
        v-bind:drag-only="dragOnly"
        v-bind:key-prop="fieldKeyProp"
        v-bind:label-prop="fieldNameProp"
        v-bind:item-grouper="fieldGroupProp"
        v-bind:orderable-hub="orderableHub"
        v-bind:visible-items="visibleItems"
        v-bind:disable-select="disableSelect"
        v-bind:readonly="readonly"
        v-on:filter-selected="onFilterSelected"
        v-on:filter-reset="onFilterReset"
        v-on:items-selected="$emit('items-selected', $event)"
        v-on:drag-start="$emit('drag-start', $event)"
        v-on:drag-move="$emit('drag-move', $event)"
        v-on:drag-end="$emit('drag-end', $event)">
        <template v-slot:default="{ item }">
            <slot v-bind:item="item" />
        </template>
    </pc-categorized-filter>
</template>

<script>
    import { DATA_FIELD_GROUP_TYPE, DATA_FIELD_TYPE } from "Types/dataFieldTypes";
    import { escapeRegex } from "Util/util";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/pcCategorizedDatafields.i18n";

    /* Parallels PC2.Common.BusinessLogic.SharedClientObjects.ClientFilteredFieldType */
    export const CLIENT_FIELD_TYPE = {
        DataField: 0,
        DataFieldGroup: 1,
        Attachment: 2,
        BuiltIn: 3,
        Communication: 4,
        Deadline: 5
    };

    export const FIELD_FILTER_CONSTS = {
        hiddenKey: "hidden",
        otherKey: "otherType",
        fieldTypeKey: "fieldType",
        groupTypeKey: "groupType"
    };

    const HIDDEN_FILTER_TYPE = {
        notReferenced: "unreferenced",
        archived: "archived"
    };

    const PROPERTIES = "Properties";
    const ORDERING_PROPERTY = "Ordering";

    export default {
        props: {
            fields: { type: [Array, Function], required: true },
            selected: { type: Array, required: true },
            dragOnly: { type: Boolean, required: false, default: false },
            hideFilters: { type: Array, required: false, default: () => [] },
            orderableHub: { type: Object, required: false, default: null },
            visibleItems: { type: Number, required: false, default: 10 },
            disableSelect: { type: Boolean, required: false, default: false },
            readonly: { type: Boolean, required: false, default: false },
            // Overrideable defaults
            fieldCategory: { type: [String, Function], required: false, default: "Category" },
            fieldKeyProp: { type: String, required: false, default: "Identifier" },
            fieldGroupProp: { type: String, required: false, default: "DataFieldGroupId" },
            fieldTypeProp: { type: String, required: false, default: "FieldTypeId" },
            fieldGroupTypeProp: { type: String, required: false, default: "GroupTypeId" },
            fieldNameProp: { type: String, required: false, default: "Name" },
            fieldReferencedProp: { type: String, required: false, default: "IsReferenced" },
            fieldArchivedProp: { type: String, required: false, default: "IsArchived" },
            includeDeadlineCategory: { type: Boolean, required: false, default: false },
            includeCommunicationCategory: { type: Boolean, required: false, default: false },
            includeAttachmentCategory: { type: Boolean, required: false, default: true },
            includeIncentivesGroupType: { type: Boolean, required: false, default: true },
            includeEnergyStorageGroupType: { type: Boolean, required: false, default: true },
            includeCustomListsGroupType: { type: Boolean, required: false, default: true },
            includePVSystemGroupType: { type: Boolean, required: false, default: true },
            includeLocationFieldType: { type: Boolean, required: false, default: false }
        },
        emits: ["items-selected", "drag-start", "drag-move", "drag-end"],
        data() {
            return {
                archivedFilterEnabled: true
            };
        },
        computed: {
            categories() {
                return [
                    {
                        label: this.hiddenCategoryLabel,
                        key: FIELD_FILTER_CONSTS.hiddenKey,
                        hidden: this.isFilterHidden(FIELD_FILTER_CONSTS.hiddenKey),
                        function: () => null, // noop, filter handled by lookup function
                        disableAll: true,
                        alwaysPassFilters: true,
                        options: [
                            {
                                label: this.unreferencedOptionLabel,
                                value: HIDDEN_FILTER_TYPE.notReferenced,
                                initiallySelected: true
                            },
                            {
                                label: this.archivedOptionLabel,
                                value: HIDDEN_FILTER_TYPE.archived,
                                enabled: this.archivedFilterEnabled, // disable when filtering out unreferenced
                                initiallySelected: true
                            }
                        ]
                    },
                    {
                        label: this.fieldTypeCategoryLabel,
                        key: FIELD_FILTER_CONSTS.fieldTypeKey,
                        function: () => null, // noop, filter handled by lookup function
                        hidden: this.isFilterHidden(FIELD_FILTER_CONSTS.fieldTypeKey),
                        alwaysPassFilters: true,
                        options: this.getFieldTypeOptions()
                    },
                    {
                        label: this.groupTypeCategoryLabel,
                        key: FIELD_FILTER_CONSTS.groupTypeKey,
                        function: () => null, // noop, filter handled by lookup function
                        hidden: this.isFilterHidden(FIELD_FILTER_CONSTS.groupTypeKey),
                        alwaysPassFilters: true,
                        options: this.getGroupTypeOptions()
                    },
                    {
                        label: this.otherCategoryLabel,
                        key: FIELD_FILTER_CONSTS.otherKey,
                        function: () => null, // noop, filter handled by lookup function
                        hidden: this.isFilterHidden(FIELD_FILTER_CONSTS.otherKey),
                        alwaysPassFilters: true,
                        options: this.getOtherOptions()
                    }
                ];
            },
            itemsFunction() {
                return typeof this.fields === "function" ? this.fields : this.getFilteredFieldsFunction(this.fields);
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        methods: {
            getFieldTypeOptions() {
                const options = [
                    { label: this.booleanOptionLabel, value: DATA_FIELD_TYPE.Boolean },
                    { label: this.choiceOptionLabel, value: DATA_FIELD_TYPE.Choice },
                    { label: this.dateOptionLabel, value: DATA_FIELD_TYPE.DateTime },
                    { label: this.decimalOptionLabel, value: DATA_FIELD_TYPE.Decimal },
                    { label: this.integerOptionLabel, value: DATA_FIELD_TYPE.Integer },
                    { label: this.stringOptionLabel, value: DATA_FIELD_TYPE.String },
                    { label: this.userOptionLabel, value: DATA_FIELD_TYPE.User }
                ];

                if (this.includeLocationFieldType) {
                    options.push({ label: this.locationOptionLabel, value: DATA_FIELD_TYPE.Location });
                }

                return options.sort((a, b) => a.label.localeCompare(b.label));
            },
            getGroupTypeOptions() {
                const options = [
                    { label: this.addressOptionLabel, value: DATA_FIELD_GROUP_TYPE.Address },
                    { label: this.contactOptionLabel, value: DATA_FIELD_GROUP_TYPE.Contact },
                    { label: this.nameOptionLabel, value: DATA_FIELD_GROUP_TYPE.Name }
                ];

                if (this.includePVSystemGroupType) {
                    options.push({ label: this.pvSystemOptionLabel, value: DATA_FIELD_GROUP_TYPE.PVSystem });
                }

                if (this.includeIncentivesGroupType) {
                    options.push({ label: this.incentiveOptionLabel, value: DATA_FIELD_GROUP_TYPE.IncentiveSelector });
                }

                if (this.includeEnergyStorageGroupType) {
                    options.push({ label: this.essOptionLabel, value: DATA_FIELD_GROUP_TYPE.EnergyStorageSystem });
                }

                if (this.includeCustomListsGroupType) {
                    options.push({ label: this.customListOptionLabel, value: DATA_FIELD_GROUP_TYPE.ExternalData });
                }

                return options;
            },
            getOtherOptions() {
                const options = [{ label: this.builtInOptionLabel, value: CLIENT_FIELD_TYPE.BuiltIn }];

                if (this.includeAttachmentCategory) {
                    options.push({ label: this.attachmentOptionLabel, value: CLIENT_FIELD_TYPE.Attachment });
                }

                if (this.includeCommunicationCategory) {
                    options.push({ label: this.communicationOptionLabel, value: CLIENT_FIELD_TYPE.Communication });
                }

                if (this.includeDeadlineCategory) {
                    options.push({ label: this.deadlineOptionLabel, value: CLIENT_FIELD_TYPE.Deadline });
                }

                return options;
            },
            isFilterHidden(filterKey) {
                return this.hideFilters.find((x) => x === filterKey) !== undefined;
            },
            onFilterSelected({ key, value, selected }) {
                if (key === FIELD_FILTER_CONSTS.hiddenKey) {
                    if (value === HIDDEN_FILTER_TYPE.notReferenced) {
                        this.archivedFilterEnabled = selected;
                    }
                }
            },
            onFilterReset() {
                this.archivedFilterEnabled = true;
            },
            getFilteredFieldsFunction(fields) {
                /**
                 * Wrapped with a callback so that changing the provided list of fields
                 * will bubble the modification down to the categorized filter component.
                 **/
                const filterFunc = this._getFilteredFields;
                return function (queryObject) {
                    return filterFunc(queryObject, fields);
                };
            },
            reRender() {
                this.$refs.categorizedFilter.reRender();
            },
            _getFilteredFields(queryObject, fields) {
                // build a proxy index array to enable stable sorts.
                const indexArray = fields.map((_, index) => index);

                const groupsIncluded = {}; // Groups filtered in
                const groupedChildren = {}; // Maps a group data field to its children
                const filteredFieldArray = [];
                const excludedGroupType = new Set();
                const groupers = {};

                const searchMatcher = queryObject.Search.Value
                    ? new RegExp(escapeRegex(queryObject.Search.Value), "i")
                    : null;

                const hiddenFilters = this._getQueryFilters(queryObject, FIELD_FILTER_CONSTS.hiddenKey);
                const fieldTypeFilters = this._getQueryFilters(queryObject, FIELD_FILTER_CONSTS.fieldTypeKey);
                const groupTypeFilters = this._getQueryFilters(queryObject, FIELD_FILTER_CONSTS.groupTypeKey);
                const otherTypeFilters = this._getQueryFilters(queryObject, FIELD_FILTER_CONSTS.otherKey);

                // If nothing is filtered from the type categories, it's interpreted as allowing all fields.
                // If any filter is active in a type category, exclude the other type categories unless there are
                // also filters on those categories.
                const hasFieldTypeFilters = fieldTypeFilters && fieldTypeFilters.size > 0;
                const hasGroupTypeFilters = groupTypeFilters && groupTypeFilters.size > 0;
                const hasOtherTypeFilters = otherTypeFilters && otherTypeFilters.size > 0;
                const anyTypeCategoriesFiltered = hasFieldTypeFilters || hasGroupTypeFilters || hasOtherTypeFilters;

                // If grouped data fields are allowed, determine which groups *can* appear. Groups
                // will only appear if some number of sub fields are allowed by filters.
                indexArray.forEach((index) => {
                    const field = fields[index];

                    if (field.IsGrouper) {
                        groupers[field[this.fieldKeyProp]] = index;
                    }

                    if (this._getFieldCategory(field) !== CLIENT_FIELD_TYPE.DataFieldGroup) {
                        // only look at data field groups
                        return;
                    } else if (hasGroupTypeFilters || !anyTypeCategoriesFiltered) {
                        if (hiddenFilters && !this._matchesHidden(field, hiddenFilters)) {
                            return;
                        }

                        if (searchMatcher && !this._matchesSearchTerm(searchMatcher, field)) {
                            return;
                        }

                        //This supports default behavior (category filter is shown)
                        if (
                            anyTypeCategoriesFiltered &&
                            groupTypeFilters &&
                            !this._matchesGroupType(field, groupTypeFilters)
                        ) {
                            excludedGroupType.add(field[this.fieldKeyProp]);
                            return;
                        }

                        groupsIncluded[field[this.fieldKeyProp]] = index;
                        filteredFieldArray.push(index);
                    }
                });

                // Loop over all non-group fields and determine if they are allowed through filters.
                indexArray.forEach((index) => {
                    const field = fields[index];
                    if (this._getFieldCategory(field) === CLIENT_FIELD_TYPE.DataFieldGroup) {
                        // look at everything besides data field groups
                        return;
                    }

                    if (hiddenFilters && !this._matchesHidden(field, hiddenFilters)) {
                        return;
                    }

                    if (searchMatcher && !this._matchesSearchTerm(searchMatcher, field)) {
                        return;
                    }

                    if (this._getFieldCategory(field) === CLIENT_FIELD_TYPE.DataField) {
                        if (field.hasOwnProperty(this.fieldGroupProp) && field[this.fieldGroupProp]) {
                            // category for groups checked above

                            if (anyTypeCategoriesFiltered && !hasGroupTypeFilters) {
                                return;
                            }

                            // handle grouped data fields separately
                            const groupKey = field[this.fieldGroupProp];

                            if (!groupsIncluded.hasOwnProperty(groupKey) && !excludedGroupType.has(groupKey)) {
                                groupsIncluded[groupKey] = groupers[groupKey];
                                filteredFieldArray.push(groupers[groupKey]);
                            }

                            // insert group item, will later insert all children below it
                            if (!groupedChildren.hasOwnProperty(groupKey)) {
                                groupedChildren[groupKey] = [];
                            }
                            groupedChildren[groupKey].push(index);
                        } else {
                            //this handles case where category filters have been hidden
                            if (this.isFilterHidden(FIELD_FILTER_CONSTS.otherKey)) {
                                //if no filters selected then everything should show
                                if (!hasFieldTypeFilters && !hasGroupTypeFilters) {
                                    filteredFieldArray.push(index);
                                } else {
                                    let matchesField = false;
                                    if (hasFieldTypeFilters) {
                                        matchesField = this._matchesType(field, fieldTypeFilters);
                                    }

                                    // support grouped data fields without data field groups (see Project Summary)
                                    let matchesGroupType = false;
                                    if (
                                        field.hasOwnProperty(this.fieldGroupTypeProp) &&
                                        field[this.fieldGroupTypeProp] &&
                                        hasGroupTypeFilters
                                    ) {
                                        matchesGroupType = this._matchesGroupType(field, groupTypeFilters);
                                    }

                                    if (!matchesField && !matchesGroupType) {
                                        return;
                                    }

                                    filteredFieldArray.push(index);
                                }
                            } else {
                                if (anyTypeCategoriesFiltered && !hasFieldTypeFilters && !hasGroupTypeFilters) {
                                    return;
                                }

                                let matchesFieldType = false;
                                let matchesGroupType = false;

                                // handling for datafields that belong to datafield group but don't appear under a
                                // datafield group (see project summary page)
                                if (field.hasOwnProperty(this.fieldGroupTypeProp) && field[this.fieldGroupTypeProp]) {
                                    if (anyTypeCategoriesFiltered && hasFieldTypeFilters) {
                                        matchesFieldType = this._matchesType(field, fieldTypeFilters);
                                    }

                                    matchesGroupType =
                                        !anyTypeCategoriesFiltered ||
                                        (hasGroupTypeFilters && this._matchesGroupType(field, groupTypeFilters));
                                } else {
                                    matchesFieldType =
                                        !anyTypeCategoriesFiltered ||
                                        (hasFieldTypeFilters && this._matchesType(field, fieldTypeFilters));
                                }

                                if (!matchesFieldType && !matchesGroupType) {
                                    return;
                                }

                                filteredFieldArray.push(index);
                            }
                        }
                    } else {
                        if (anyTypeCategoriesFiltered && !hasOtherTypeFilters) {
                            return;
                        }

                        // check category for attachments and built-in fields
                        if (hasOtherTypeFilters && !this._matchesCategory(field, otherTypeFilters)) {
                            return;
                        }

                        //Handling for non-datafield groups (like attachments and communications in the reports editor)
                        if (field.hasOwnProperty(this.fieldGroupProp) && field[this.fieldGroupProp]) {
                            const groupKey = field[this.fieldGroupProp];

                            if (!groupsIncluded.hasOwnProperty(groupKey)) {
                                groupsIncluded[groupKey] = groupers[groupKey];
                                filteredFieldArray.push(groupers[groupKey]);
                            }

                            if (!groupedChildren.hasOwnProperty(groupKey)) {
                                groupedChildren[groupKey] = [];
                            }
                            groupedChildren[groupKey].push(index);
                        } else {
                            if (!groupsIncluded.hasOwnProperty(field[this.fieldKeyProp])) {
                                groupsIncluded[field[this.fieldKeyProp]] = index;
                                filteredFieldArray.push(index);
                            }
                        }
                    }
                });

                // sort all non-subfields alphabetically
                filteredFieldArray.sort(this._compareByFieldNameAndOrdering);

                // insert all group subfields below the appropriate group to keep expected sort
                this._insertGroupedFields(filteredFieldArray, groupedChildren);

                return {
                    Data: filteredFieldArray.map((i) => fields[i])
                };
            },
            _matchesHidden(field, filterSet) {
                if (field[this.fieldArchivedProp] && !filterSet.has(HIDDEN_FILTER_TYPE.archived)) {
                    return false;
                }

                //communication and deadlines should not be subject to the "referenced on current forms" filter
                if (
                    this._getFieldCategory(field) === CLIENT_FIELD_TYPE.Communication ||
                    this._getFieldCategory(field) === CLIENT_FIELD_TYPE.Deadline
                ) {
                    return true;
                }

                if (!field[this.fieldReferencedProp] && !filterSet.has(HIDDEN_FILTER_TYPE.notReferenced)) {
                    return false;
                }

                return true;
            },
            _matchesCategory(field, filterSet) {
                const fieldCategory = this._getFieldCategory(field);
                return filterSet.has(fieldCategory);
            },
            _matchesType(field, filterSet) {
                const fieldType = field[this.fieldTypeProp];
                return filterSet.has(fieldType);
            },
            _matchesGroupType(field, filterSet) {
                const fieldType = field[this.fieldGroupTypeProp];
                return filterSet.has(fieldType);
            },
            _getFieldCategory(field) {
                if (typeof this.fieldCategory === "function") {
                    return this.fieldCategory(field) ?? CLIENT_FIELD_TYPE.BuiltIn;
                } else {
                    return field[this.fieldCategory] ?? CLIENT_FIELD_TYPE.BuiltIn;
                }
            },
            _getQueryFilters(queryObject, key) {
                const filterObj = queryObject.CategorizedFilters.find((filterObj) => filterObj.Key == key);
                return filterObj ? new Set(filterObj.Values) : null;
            },
            _matchesSearchTerm(searchMatcher, field) {
                const matchName = field[this.fieldNameProp].toLowerCase();
                return searchMatcher.test(matchName);
            },
            _compareByFieldNameAndOrdering(a, b) {
                const fieldA = this.fields[a];
                const fieldB = this.fields[b];

                let posA = Number.MAX_SAFE_INTEGER;
                let posB = Number.MAX_SAFE_INTEGER;

                if (
                    fieldA.hasOwnProperty(PROPERTIES) &&
                    fieldA.Properties !== null &&
                    fieldA.Properties.hasOwnProperty(ORDERING_PROPERTY)
                ) {
                    posA = fieldA.Properties.Ordering;
                }
                if (
                    fieldB.hasOwnProperty(PROPERTIES) &&
                    fieldB.Properties !== null &&
                    fieldB.Properties.hasOwnProperty(ORDERING_PROPERTY)
                ) {
                    posB = fieldB.Properties.Ordering;
                }

                if (posA < posB) {
                    return -1;
                }
                if (posA > posB) {
                    return 1;
                }

                const fieldNameA = fieldA[this.fieldNameProp];
                const fieldNameB = fieldB[this.fieldNameProp];
                return fieldNameA.toString().localeCompare(fieldNameB.toString());
            },
            _insertGroupedFields(filteredFields, groupedChildren) {
                // looping back-to-front makes this a bit cleaner to read
                for (let index = filteredFields.length - 1; index >= 0; index--) {
                    const field = this.fields[filteredFields[index]];

                    if (groupedChildren.hasOwnProperty(field[this.fieldKeyProp])) {
                        // sort children and insert them immediately after the parent
                        const children = groupedChildren[field[this.fieldKeyProp]];
                        children.sort(this._compareByFieldNameAndOrdering);
                        for (let childIndex = children.length - 1; childIndex >= 0; childIndex--) {
                            filteredFields.splice(index + 1, 0, children[childIndex]);
                        }
                    }
                }
            }
        }
    };
</script>
